import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, message, Input, Modal } from 'antd';
import { SearchOutlined, ReloadOutlined, FileTextOutlined, UnlockOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

interface AnnouncementItem {
  id: number;
  project_no: string;
  project_name: string;
  bidding_unit: string;
  open_bid_time: string;
  is_bidopening: number;
}

interface SupplierItem {
  supplier_name: string;
  quote_amount: string;
  quote_amount_upper: string;
  bidding_url?: string;
}

export default function TodayOpenBid() {
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [listScope, setListScope] = useState<'today' | 'history'>('today');
  const [modalVisible, setModalVisible] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState<AnnouncementItem | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([]);
  const [supplierCount, setSupplierCount] = useState(0);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isDecrypted, setIsDecrypted] = useState(false);
  const [canStartDecrypt, setCanStartDecrypt] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchAnnouncements();
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const fetchAnnouncements = async (scope: 'today' | 'history' = listScope) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      params.append('scope', scope);
      if (searchKeyword) {
        params.append('keyword', searchKeyword);
      }
      
      const resp = await fetch(`/api/admin/today-open-bid?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnouncements(resp.data || []);
      } else {
        message.error(resp?.error || '获取数据失败');
      }
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAnnouncements(listScope);
  };

  const handleSwitchScope = (scope: 'today' | 'history') => {
    setListScope(scope);
    fetchAnnouncements(scope);
  };

  // 格式化时间
  const formatDateTime = (time: string | null) => {
    if (!time) return '-';
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
  };

  // 计算倒计时
  const calculateCountdown = (openBidTime: string) => {
    const now = dayjs();
    const openTime = dayjs(openBidTime);
    const diff = openTime.diff(now, 'second');
    
    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    
    return {
      hours: Math.floor(diff / 3600),
      minutes: Math.floor((diff % 3600) / 60),
      seconds: diff % 60,
    };
  };

  const getFileNameFromUrl = (url: string) => {
    try {
      const last = url.split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
      return last || '投标文件';
    } catch {
      return '投标文件';
    }
  };

  const handleDownloadBiddingFile = (url: string, supplierName?: string) => {
    if (!url) {
      message.warning('没有可下载的投标文件');
      return;
    }

    const fileName = getFileNameFromUrl(url);
    const safeSupplier = supplierName ? supplierName.replace(/[\\/:*?"<>|]+/g, '_') : '';
    const downloadName = safeSupplier ? `${safeSupplier}_${fileName}` : fileName;

    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // 打开弹窗
  const handleOpenModal = async (record: AnnouncementItem) => {
    setCurrentAnnouncement(record);
    setIsDecrypted(record.is_bidopening === 1);
    setModalVisible(true);

    // 获取供应商数据
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/today-open-bid/suppliers?project_no=${encodeURIComponent(record.project_no)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setSupplierCount(resp.count || 0);
        if (record.is_bidopening === 1) {
          // 已解密，显示供应商数据
          setSuppliers(resp.data || []);
        } else {
          // 未解密，只显示数量
          setSuppliers([]);
          // 计算倒计时
          const cd = calculateCountdown(record.open_bid_time);
          setCountdown(cd);
          setCanStartDecrypt(cd.hours === 0 && cd.minutes === 0 && cd.seconds === 0);
          
          // 如果还未到开标时间，启动倒计时
          if (cd.hours > 0 || cd.minutes > 0 || cd.seconds > 0) {
            countdownIntervalRef.current = setInterval(() => {
              const newCd = calculateCountdown(record.open_bid_time);
              setCountdown(newCd);
              if (newCd.hours === 0 && newCd.minutes === 0 && newCd.seconds === 0) {
                setCanStartDecrypt(true);
                if (countdownIntervalRef.current) {
                  clearInterval(countdownIntervalRef.current);
                  countdownIntervalRef.current = null;
                }
              }
            }, 1000);
          }
        }
      } else {
        message.error(resp?.error || '获取供应商数据失败');
      }
    } catch (err) {
      message.error('获取供应商数据失败');
    }
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setCurrentAnnouncement(null);
    setSuppliers([]);
    setSupplierCount(0);
    setCountdown({ hours: 0, minutes: 0, seconds: 0 });
    setIsDecrypted(false);
    setCanStartDecrypt(false);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  // 开始解密
  const handleStartDecrypt = async () => {
    if (!currentAnnouncement) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/today-open-bid/decrypt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ project_no: currentAnnouncement.project_no }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('解密成功');
        setIsDecrypted(true);
        
        // 更新列表中的数据
        setAnnouncements(prev => prev.map(item => 
          item.project_no === currentAnnouncement.project_no 
            ? { ...item, is_bidopening: 1 }
            : item
        ));

        // 获取供应商数据
        const supplierResp = await fetch(`/api/admin/today-open-bid/suppliers?project_no=${encodeURIComponent(currentAnnouncement.project_no)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }).then(r => r.json());

        if (supplierResp && supplierResp.ok) {
          setSuppliers(supplierResp.data || []);
        }
      } else {
        message.error(resp?.error || '解密失败');
      }
    } catch (err) {
      message.error('解密失败');
    }
  };

  const columns: ColumnsType<AnnouncementItem> = [
    {
      title: '项目编号',
      dataIndex: 'project_no',
      key: 'project_no',
      width: 180,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 280,
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '采购人',
      dataIndex: 'bidding_unit',
      key: 'bidding_unit',
      width: 200,
      ellipsis: true,
    },
    {
      title: '开标时间',
      dataIndex: 'open_bid_time',
      key: 'open_bid_time',
      width: 180,
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDateTime(text)}</span>
    },
    {
      title: '解密',
      key: 'decrypt',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (record.is_bidopening === 1) {
          return (
            <span 
              style={{ color: '#10b981', cursor: 'pointer' }}
              onClick={() => handleOpenModal(record)}
            >
              已解密
            </span>
          );
        } else {
          return (
            <Button
              type="primary"
              size="small"
              icon={<UnlockOutlined />}
              onClick={() => handleOpenModal(record)}
              style={{ borderRadius: 8 }}
            >
              解密
            </Button>
          );
        }
      },
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <FileTextOutlined />
          </div>
          <div>
            <h2>{listScope === 'today' ? '今日开标项目' : '历史开标项目'}</h2>
            <p>查看开标项目并进行解密操作</p>
          </div>
        </div>
        
        <Space size="middle">
          <Space size="small">
            <Button
              type={listScope === 'today' ? 'primary' : 'default'}
              onClick={() => handleSwitchScope('today')}
              style={{ borderRadius: 10 }}
            >
              今日开标项目
            </Button>
            <Button
              type={listScope === 'history' ? 'primary' : 'default'}
              onClick={() => handleSwitchScope('history')}
              style={{ borderRadius: 10 }}
            >
              历史开标项目
            </Button>
          </Space>
          <Input
            placeholder="搜索项目编号/名称"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => fetchAnnouncements(listScope)}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{announcements.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={announcements}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 900 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>

      {/* 远程开标大厅弹窗 */}
      <Modal
        title={
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            远程开标大厅
          </div>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        destroyOnClose
        style={{ maxWidth: '90vw' }}
        bodyStyle={{ maxHeight: '80vh', overflow: 'auto' }}
      >
        {currentAnnouncement && (
          <div>
            {!isDecrypted ? (
              // 未解密状态：显示倒计时和开始解密按钮
              <div style={{ padding: '20px 0', textAlign: 'center' }}>
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontSize: 16, color: '#6b7280', marginBottom: 16 }}>
                    开标倒计时
                  </div>
                  <div style={{ 
                    fontSize: 48, 
                    fontWeight: 700, 
                    color: '#3b82f6',
                    fontFamily: 'monospace',
                    letterSpacing: 8
                  }}>
                    {String(countdown.hours).padStart(2, '0')}:
                    {String(countdown.minutes).padStart(2, '0')}:
                    {String(countdown.seconds).padStart(2, '0')}
                  </div>
                </div>
                
                <div style={{ fontSize: 16, color: '#374151', marginBottom: 32 }}>
                  当前投标情况（上传投标文件家数）：<span style={{ color: '#3b82f6', fontWeight: 600 }}>{supplierCount}</span>
                </div>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<UnlockOutlined />}
                  onClick={handleStartDecrypt}
                  disabled={!canStartDecrypt}
                  style={{ 
                    borderRadius: 10,
                    height: 48,
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  开始解密
                </Button>
              </div>
            ) : (
              // 已解密状态：显示当前报名情况
              <div>
                <div style={{ marginBottom: 16, padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: 8 }}>
                  <div style={{ marginBottom: 8, fontSize: 14, color: '#374151' }}>
                    <strong>项目名称：</strong>{currentAnnouncement.project_name}
                  </div>
                  <div style={{ fontSize: 14, color: '#374151' }}>
                    <strong>项目编号：</strong>{currentAnnouncement.project_no}
                  </div>
                </div>

                <div style={{ marginBottom: 16, fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
                  当前报名情况
                </div>

                <Table
                  dataSource={suppliers}
                  rowKey={(record, index) => `${record.supplier_name}-${index}`}
                  pagination={false}
                  scroll={{ y: 400 }}
                  columns={[
                    {
                      title: '投标供应商',
                      dataIndex: 'supplier_name',
                      key: 'supplier_name',
                      width: 250,
                    },
                    {
                      title: '投标报价',
                      dataIndex: 'quote_amount',
                      key: 'quote_amount',
                      width: 150,
                      align: 'right',
                      render: (text) => (text ? `${text}` : '-'),
                    },
                    {
                      title: '下载投标文件',
                      dataIndex: 'bidding_url',
                      key: 'bidding_url',
                      width: 160,
                      align: 'center',
                      render: (_: unknown, record: SupplierItem) => {
                        const url = record.bidding_url || '';
                        if (!url) return '-';
                        return (
                          <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadBiddingFile(url, record.supplier_name)}
                          >
                            下载
                          </Button>
                        );
                      },
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

